import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store'

export async function authGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const authStore = useAuthStore()

  // Check if route explicitly doesn't require auth
  if (to.meta.requiresAuth === false) {
    next()
    return
  }

  // Public routes - always allow access
  const publicRoutes = ['/login', '/callback']
  if (publicRoutes.includes(to.path)) {
    next()
    return
  }

  // Only check auth state if not already authenticated
  // This prevents redundant checks on every navigation
  // The token refresh composable handles keeping the session alive
  if (!authStore.isAuthenticated) {
    // Wait for auth to be checked/restored from session
    // This is critical for page refreshes to not lose auth state
    await authStore.checkAuth()
  }

  if (!authStore.isAuthenticated) {
    // Redirect to login with returnTo parameter
    next({
      path: '/login',
      query: { returnTo: to.fullPath },
    })
    return
  }

  // Check permissions if route requires them
  const requiredPermission = to.meta.permission as string | undefined
  if (requiredPermission && !authStore.hasPermission(requiredPermission)) {
    next({ path: '/forbidden' })
    return
  }

  // Check roles if route requires them (database roles, not Auth0 permissions)
  const requiredRole = to.meta.role as string | undefined
  if (requiredRole && !authStore.hasRole(requiredRole)) {
    next({ path: '/forbidden' })
    return
  }

  // Check for multiple roles (if any role matches, allow access)
  const requiredRoles = to.meta.roles as string[] | undefined
  if (requiredRoles && requiredRoles.length > 0) {
    const hasAnyRole = requiredRoles.some(role => authStore.hasRole(role))
    if (!hasAnyRole) {
      next({ path: '/forbidden' })
      return
    }
  }

  // User is authenticated and has required permissions
  next()
}
